"use client";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import Image from "next/image";
import { Offer, ProductData } from "@/services/dataTypes";
import OfferDuration from "./OfferDuration";
import { getBaseImageUrl } from "@/constants/hooks";
import Link from "next/link";
import { faCheck, faCopy, faTimes, faArrowRight, faShieldHalved, FontAwesomeIcon } from "@/constants/icons";

interface Props {
    data:              Offer | ProductData;
    onClose:           () => void;
    domain:            string;
    merchantHref:      string;
    finalDiscountTag?: string | null;
    merchantImg?:      string | null;
    productImg?:       string | null;
}

const SimpleOfferModal = ({
    data, onClose, domain, merchantHref,
    finalDiscountTag, merchantImg, productImg,
}: Props) => {
    const [mounted, setMounted] = useState(false);
    const [copied,  setCopied]  = useState(false);
    const [imgSrc,  setImgSrc]  = useState("");

    useEffect(() => {
        setImgSrc(getBaseImageUrl(domain, productImg || merchantImg, ""));
        setMounted(true);
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = ""; };
    }, [domain, productImg, merchantImg]);

    /* close on Escape */
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    const handleCopy = () => {
        if (!data?.coupon_code) return;
        navigator.clipboard?.writeText(data.coupon_code).catch(() => {
            const ta = document.createElement("textarea");
            ta.value = data.coupon_code!;
            document.body.appendChild(ta); ta.select();
            document.execCommand("copy"); document.body.removeChild(ta);
        });
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (!mounted) return null;

    const absoluteUrl = `/${(data?.url || "").replace(/^\/+/, "")}`;

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4
                       bg-black/60 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className="relative w-full max-w-md bg-white rounded-2xl overflow-hidden
                           shadow-[0_24px_60px_-8px_rgba(0,0,0,0.3)]"
                onClick={e => e.stopPropagation()}
            >
                {/* Top accent */}
                <div className="h-[3px] bg-gradient-to-r from-[#D61C4E] via-[#D61C4E]/60 to-transparent" />

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                    <OfferDuration endDate={data?.end_date} />
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-xl
                                   bg-slate-100 hover:bg-[#D61C4E] hover:text-white
                                   text-slate-500 transition-all duration-200 active:scale-90"
                        aria-label="Close"
                    >
                        <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                    </button>
                </div>

                {/* Image + badge */}
                <div className="relative bg-gradient-to-b from-slate-50 to-white px-8 py-7
                                flex items-center justify-center min-h-[160px] border-b border-slate-100">
                    {finalDiscountTag && (
                        <span className="absolute top-4 left-4 bg-[#D61C4E] text-white
                                         text-[9px] font-black px-3 py-1.5 rounded-lg
                                         shadow-md uppercase tracking-wider">
                            {finalDiscountTag}
                        </span>
                    )}
                    <Image
                        src={imgSrc}
                        alt={data?.offer_title || "Offer"}
                        width={160}
                        height={120}
                        className="object-contain drop-shadow-sm max-h-[120px] w-auto"
                        unoptimized
                    />
                </div>

                {/* Content */}
                <div className="px-6 py-5 space-y-4">
                    <h3 className="text-[15px] font-black text-slate-900 leading-snug text-center">
                        {data?.offer_title}
                    </h3>

                    {data?.offer_detail && (
                        <div
                            className="text-slate-500 text-[12px] leading-relaxed text-center
                                       max-h-16 overflow-hidden line-clamp-3"
                            dangerouslySetInnerHTML={{ __html: data.offer_detail }}
                        />
                    )}

                    {/* Coupon code box */}
                    {data?.coupon_code ? (
                        <div className="flex items-center gap-2 px-4 py-3 rounded-xl
                                        bg-slate-50 border border-dashed border-slate-300">
                            <span className="flex-1 font-mono font-black text-[14px] text-slate-800
                                             tracking-widest uppercase truncate">
                                {data.coupon_code}
                            </span>
                            <button
                                onClick={handleCopy}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg
                                            font-black text-[10px] uppercase tracking-widest
                                            transition-all duration-300 active:scale-95 shrink-0
                                            ${copied
                                                ? "bg-green-500 text-white"
                                                : "bg-[#D61C4E] text-white hover:bg-slate-900"
                                            }`}
                            >
                                <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="w-3 h-3" />
                                {copied ? "Copied!" : "Copy"}
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center gap-2 py-3 rounded-xl
                                        bg-[#D61C4E]/5 border border-[#D61C4E]/15">
                            <FontAwesomeIcon icon={faShieldHalved} className="w-3.5 h-3.5 text-[#D61C4E]" />
                            <p className="text-[#D61C4E] font-black text-[10px] uppercase tracking-widest">
                                Auto discount at checkout
                            </p>
                        </div>
                    )}

                    {/* Primary CTA */}
                    <Link
                        href={absoluteUrl}
                        target="_blank"
                        rel="nofollow noopener"
                        className="group flex items-center justify-center gap-2.5 w-full py-3.5
                                   bg-[#D61C4E] hover:bg-slate-900 text-white rounded-xl
                                   font-black text-[11px] uppercase tracking-widest
                                   shadow-lg shadow-[#D61C4E]/20 transition-all duration-300
                                   active:scale-95 no-underline"
                    >
                        {data?.coupon_code ? "Shop with Coupon" : "Get This Deal"}
                        <FontAwesomeIcon icon={faArrowRight}
                            className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                </div>

                {/* Footer */}
                <div className="bg-slate-50 border-t border-slate-100 px-6 py-3
                                flex items-center justify-center gap-2">
                    <FontAwesomeIcon icon={faShieldHalved} className="w-3.5 h-3.5 text-[#D61C4E]" />
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.25em]">
                        Verified &amp; Working · {new Date().getFullYear()}
                    </p>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default SimpleOfferModal;
